import { Button, Checkbox, Modal, Typography, message } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFileArrowDown } from '@fortawesome/pro-regular-svg-icons'
import { Dayjs } from 'dayjs'
import { useState } from 'react'
import { useSavedState } from '../../hooks/useSavedState'
import {
  CrewDaySchedulePrintOptions,
  defaultPrintOptions,
  PRINT_OPTIONS_STORAGE_KEY,
  sanitisePrintOptions
} from './crewDaySchedulePrintModel'
import { usePrintCrewDaySchedulesMutation } from './scheduleQueries'

type Props = {
  selectedDate: Dayjs
}

const DownloadCrewSchedules = ({ selectedDate }: Props) => {
  const [open, setOpen] = useState(false)
  const [savedOptions, setSavedOptions] = useSavedState<CrewDaySchedulePrintOptions>(
    PRINT_OPTIONS_STORAGE_KEY,
    defaultPrintOptions
  )
  const options = sanitisePrintOptions(savedOptions)

  const { mutate: printSchedules, isLoading } = usePrintCrewDaySchedulesMutation(() => {
    message.info('Nothing is scheduled on this date, so there is nothing to print.')
    setOpen(false)
  })

  const onDownload = () => {
    printSchedules(
      {
        date: selectedDate.format('YYYY-MM-DD'),
        includeConsultations: options.includeConsultations,
        includeTasks: options.includeTasks
      },
      { onSuccess: (result) => { if (result.archive !== null) { setOpen(false) } } }
    )
  }

  return (
    <>
      <Button
        icon={<FontAwesomeIcon icon={faFileArrowDown} />}
        type="default"
        style={{ fontWeight: 'bold' }}
        onClick={() => setOpen(true)}
      >
        Download Schedules
      </Button>

      <Modal
        title={`Download crew schedules — ${selectedDate.format('ddd, MMM D')}`}
        open={open}
        onCancel={() => setOpen(false)}
        confirmLoading={isLoading}
        okText={isLoading ? 'Preparing…' : 'Download'}
        onOk={onDownload}
        destroyOnHidden
      >
        <Typography.Paragraph type="secondary">
          One PDF per crew lead, delivered as a zip. Every crew lead scheduled on this date is
          included — the calendar filters do not narrow it.
        </Typography.Paragraph>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Checkbox checked disabled>Jobs</Checkbox>
          <Checkbox
            checked={options.includeConsultations}
            onChange={(e) => setSavedOptions({ ...options, includeConsultations: e.target.checked })}
          >
            Consultations
          </Checkbox>
          <Checkbox
            checked={options.includeTasks}
            onChange={(e) => setSavedOptions({ ...options, includeTasks: e.target.checked })}
          >
            Tasks
          </Checkbox>
        </div>
      </Modal>
    </>
  )
}

export default DownloadCrewSchedules
